import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = [["pilot", "Pilot (hidden)"], ["base", "Base"], ["pro", "Pro"]];
const STATUS_FG = { pending: "#0091D5", used: "#2E7D5B", expired: "#5A6B75", cancelled: "#5A6B75" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : e.message;
};

function NewInviteForm({ onCreated, onClose }) {
  const [kind, setKind] = useState("existing");
  const [assocs, setAssocs] = useState([]);
  const [aid, setAid] = useState("");
  const [admins, setAdmins] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [plan, setPlan] = useState("pilot");
  const [prices, setPrices] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/subscription-invites/prices`)
      .then((r) => setPrices(r.data)).catch(() => setPrices({}));
  }, []);
  useEffect(() => {
    if (prices && prices[plan] != null) setPrice(String(prices[plan]));
  }, [plan, prices]);

  useEffect(() => {
    axios.get(`${API}/admin/associations`, { params: { include_inactive: false } })
      .then((r) => setAssocs(r.data || [])).catch(() => setAssocs([]));
  }, []);
  useEffect(() => {
    setAdmins([]); setEmail(""); setSimilar([]);
    if (!aid) return;
    axios.get(`${API}/admin/subscription-invites/similar`, { params: { association_id: aid } })
      .then((r) => setSimilar(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/subscription-invites/eligible-admins`, { params: { association_id: aid } })
      .then((r) => { setAdmins(r.data || []); if ((r.data || []).length === 1) setEmail(r.data[0].email); })
      .catch((e) => toast.error(errMsg(e)));
  }, [aid]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { kind, email, plan, price_usd: Number(price), ...(kind === "existing" ? { association_id: aid } : { full_name: fullName }) };
      const r = await axios.post(`${API}/admin/subscription-invites`, body);
      toast.success(`Invite emailed to ${r.data.email}`);
      onCreated(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const priceOk = /^\d+$/.test(price) && Number(price) >= 1 && Number(price) <= 9999;
  const ready = email && plan && priceOk && (kind === "new" ? fullName.trim().length >= 2 : aid);
  const field = "w-full border border-[#C9D6E2] rounded-[3px] px-2 py-1 text-[12.5px] text-navy";

  return (
    <div className="mt-3 border border-sky rounded-[4px] p-3 bg-[#F2F9FD]" data-testid="invite-form">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-bold text-navy uppercase tracking-[0.05em]">New Subscription Invite</div>
        <button type="button" onClick={onClose} data-testid="invite-form-close" className="text-slate hover:text-navy"><X size={14} /></button>
      </div>
      <div className="flex gap-4 mb-2 text-[12.5px] text-navy">
        {[["existing", "Existing association"], ["new", "New association"]].map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={kind === k} onChange={() => { setKind(k); setEmail(""); }} data-testid={`invite-kind-${k}`} /> {l}
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {kind === "existing" ? (
          <>
            <select className={field} value={aid} onChange={(e) => setAid(e.target.value)} data-testid="invite-association-select">
              <option value="">Select association…</option>
              {assocs.map((a) => <option key={a.aid} value={a.aid}>{a.name} — {a.tenant_name}</option>)}
            </select>
            <select className={field} value={email} onChange={(e) => setEmail(e.target.value)} disabled={!aid} data-testid="invite-admin-select">
              <option value="">{aid && admins.length === 0 ? "No active Admins" : "Select Admin…"}</option>
              {admins.map((u) => <option key={u.user_id} value={u.email}>{u.full_name} ({u.email})</option>)}
            </select>
          </>
        ) : (
          <>
            <input className={field} placeholder="Admin full name" value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="invite-full-name" />
            <input className={field} placeholder="Admin email" type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} data-testid="invite-email" />
          </>
        )}
        <div className="flex gap-2">
          <select className={field} value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="invite-plan-select">
            {PLANS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[12.5px] text-navy shrink-0" title="Prefilled from Payment Settings — adjust for this invite only">
            $<input
              className={`${field} w-[64px] text-right`}
              inputMode="numeric"
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
              data-testid="invite-price-input"
            />/mo
          </label>
        </div>
        <button type="button" disabled={!ready || busy} onClick={submit} data-testid="invite-send-btn"
          className="inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-white bg-coral rounded-full px-3 py-1 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Email Invite
        </button>
      </div>
      {kind === "existing" && similar.length > 0 && (
        <div className="mt-2 border border-coral bg-[#FDF5F2] rounded-[3px] px-2 py-1.5 text-[12px] text-coral" data-testid="invite-similar-warning">
          <strong>Possible duplicate:</strong> already subscribed —{" "}
          {similar.map((s) => `${s.name}${s.address ? ` (${s.address})` : ""} · ${s.reason}`).join("; ")}.
          Check this isn't the same association before sending.
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate">Single-use link, valid 5 days. A reminder email goes out automatically 3 days before an unused link expires. The price is prefilled from Payment Settings for the chosen plan; any change here applies to this invite only and is locked in when you send. Sending replaces any earlier pending invite for the same association.</div>
    </div>
  );
}

export function SubscriptionInvitesPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const shown = showHistory ? rows : rows.filter((r) => r.status === "pending");
  const historyCount = rows.length - rows.filter((r) => r.status === "pending").length;

  const load = () => axios.get(`${API}/admin/subscription-invites`).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      toast.success(`Link copied — send it to ${inv.email}`);
    } catch {
      window.prompt("Copy this invite link:", inv.invite_url);
    }
  };

  const act = async (inv, action) => {
    setBusyId(inv.invite_id);
    try {
      await axios.post(`${API}/admin/subscription-invites/${inv.invite_id}/${action}`);
      toast.success(action === "resend" ? `New link emailed to ${inv.email}` : "Invite cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-6" data-testid="subscription-invites-panel">
      <div className="flex items-baseline justify-between border-b-2 border-coral pb-1">
        <span className="text-[15px] font-bold text-navy">Subscription Invites</span>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-navy cursor-pointer" data-testid="invites-show-history">
            <input type="checkbox" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} className="accent-[#1C4E80]" />
            Show History{historyCount ? ` (${historyCount})` : ""}
          </label>
          {!open && (
            <button type="button" onClick={() => setOpen(true)} data-testid="invite-new-btn"
              className="text-[12px] font-semibold text-white bg-coral rounded-full px-3 py-1 hover:opacity-90 transition-opacity">
              + New Invite
            </button>
          )}
        </div>
      </div>
      {open && <NewInviteForm onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {shown.length === 0 ? (
        <div className="mt-3 text-slate italic text-[13px]" data-testid="invites-empty">
          {rows.length === 0 ? "No invites sent yet." : "No pending invites. Tick Show History to see used, expired and cancelled invites."}
        </div>
      ) : (
        <table className="u-table u-table-dense mt-3" style={{ tableLayout: "fixed", width: "100%" }} data-testid="invites-table">
          <thead>
            <tr style={{ background: "#1C4E80", color: "#fff" }}>
              <th className="text-left" style={{ width: "20%" }}>Association</th>
              <th className="text-left" style={{ width: "22%" }}>Admin</th>
              <th className="text-left" style={{ width: "10%" }}>Plan</th>
              <th className="text-left" style={{ width: "21%" }}>Status</th>
              <th className="text-left" style={{ width: "12%" }}>Expires</th>
              <th className="text-left" style={{ width: "15%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.invite_id} data-testid={`invite-row-${r.invite_id}`}>
                <td className="text-navy font-semibold">{r.association_name || <span className="italic font-normal text-slate">New association</span>}</td>
                <td>{r.full_name ? `${r.full_name} · ` : ""}{r.email}</td>
                <td>{r.plan_label} · ${r.price_usd}/mo</td>
                <td>
                  <span className="text-[11px] font-semibold capitalize" style={{ color: STATUS_FG[r.status] }} data-testid={`invite-status-${r.invite_id}`}>{r.status}</span>
                  {r.ack_at && <span className="ml-2 text-[10.5px] text-slate" title={r.ack_text}>acknowledged {fmtDate(r.ack_at)}</span>}
                  {r.sop_ack_at && (
                    <span className="ml-2 text-[10.5px] text-slate" data-testid={`invite-sop-ack-${r.invite_id}`}
                      title={`Statement of Purpose acknowledged ${new Date(r.sop_ack_at).toLocaleString()}`}>
                      · SoP {new Date(r.sop_ack_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </td>
                <td>
                  {r.status === "used" ? `Used ${fmtDate(r.used_at)}` : fmtDate(r.expires_at)}
                  {r.reminder_sent_at && r.status !== "used" && (
                    <span className="block text-[10.5px] text-slate" data-testid={`invite-reminder-${r.invite_id}`}>reminder sent {fmtDate(r.reminder_sent_at)}</span>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {r.invite_url && (
                    <button type="button" onClick={() => copyLink(r)} data-testid={`invite-copy-${r.invite_id}`}
                      className="text-[12px] text-navy hover:text-sky mr-3">Copy link</button>
                  )}
                  {(r.status === "pending" || r.status === "expired") && (
                    <button type="button" disabled={busyId === r.invite_id} onClick={() => act(r, "resend")} data-testid={`invite-resend-${r.invite_id}`}
                      className="text-[12px] text-sky hover:text-navy mr-3 disabled:opacity-50">Resend</button>
                  )}
                  {r.status === "pending" && (
                    <button type="button" disabled={busyId === r.invite_id} onClick={() => act(r, "cancel")} data-testid={`invite-cancel-${r.invite_id}`}
                      className="text-[12px] text-coral hover:text-navy disabled:opacity-50">Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
